package one.healthium.settings.ui.settings

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import loco.ABOUT_PRODUCT_SCREEN_TITLE_FORMAT
import loco.LEGAL_PRODUCT_SCREEN_TITLE
import loco.Res
import loco.SETTINGS_ABOUT_PRODUCT_LABEL
import loco.SETTINGS_CONTACT_US_LABEL
import loco.SETTINGS_DELETE_ACCOUNT_ALERT_MESSAGE
import loco.SETTINGS_DELETE_ACCOUNT_ALERT_TITLE
import loco.SETTINGS_DELETE_ACCOUNT_LABEL
import loco.SETTINGS_DELETE_ACCOUNT_NEGATIVE_BUTTON
import loco.SETTINGS_DELETE_ACCOUNT_POSITIVE_BUTTON
import loco.SETTINGS_GENERAL_SECTION
import loco.SETTINGS_LEGAL_PRODUCT_LABEL
import loco.SETTINGS_LOGOUT_LABEL
import loco.SETTINGS_MANAGE_SUBSCRIPTION_LABEL
import loco.SETTINGS_MEAL_PLAN_LABEL
import loco.SETTINGS_METRICS_SYSTEM_LABEL
import loco.SETTINGS_PERIOD_TRACKER_LABEL
import loco.SETTINGS_PROFILE_AGE_LABEL
import loco.SETTINGS_PROFILE_HEIGHT_LABEL
import loco.SETTINGS_PROFILE_INITIAL_WEIGHT_LABEL
import loco.SETTINGS_PROFILE_NAME_LABEL
import loco.SETTINGS_PROFILE_SECTION
import loco.SETTINGS_PROFILE_TARGET_WEIGHT_LABEL
import loco.SETTINGS_SCREEN_TITLE
import one.healthium.base.browser.Browser
import one.healthium.base.launcher.LocalPlatformLauncher
import one.healthium.base.preferences.AppPreferences
import one.healthium.base.utils.BundleUtils
import one.healthium.healthKit.ui.ConnectBannerHelper
import one.healthium.settings.ui.components.ArrowIcon
import one.healthium.settings.ui.components.Section
import one.healthium.settings.ui.components.SettingsItem
import one.healthium.ui.compose.components.AlertButtonType
import one.healthium.ui.compose.components.AppAlertDialog
import one.healthium.ui.compose.components.AppBackNavigationButton
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppTopBar
import one.healthium.ui.compose.components.LocalGlobalProgressIndicator
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.red_1
import one.primehealth.analytics.AppAnalytics
import one.primehealth.analytics.events.Events
import one.primehealth.app.BaseAppConfig
import one.primehealth.app.Flavour
import one.primehealth.app.TargetPlatform
import one.primehealth.app.targetPlatform
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel
import kotlin.time.Clock

interface SettingsScreenEventDelegate {
    fun onNameClicked()
    fun onAgeClicked()
    fun onHeightClicked()
    fun onInitialWeightClicked()
    fun onTargetWeightClicked()
    suspend fun onContactUsClicked()
    fun onSettingsMenuClicked(menuId: String, title: String)
    fun onMealPlanClicked()
    fun onHealthKitClicked()
    fun onBackClicked()
}

@Composable
fun SettingsScreen(eventDelegate: SettingsScreenEventDelegate) {
    val viewModel = koinViewModel<SettingsViewModel>()
    val flavour = koinInject<Flavour>()
    val browser = koinInject<Browser>()
    val state by viewModel.state.collectAsState()
    val scope = rememberCoroutineScope()
    val globalProgressIndicator = LocalGlobalProgressIndicator.current
    val platformLauncher = LocalPlatformLauncher.current
    val baseAppConfig = koinInject<BaseAppConfig>()
    val uriHandler = LocalUriHandler.current
    val appAnalytics = koinInject<AppAnalytics>()

    globalProgressIndicator.value = state.logoutInProgress

    val showAccountDeleteConfirmation = remember { mutableStateOf(false) }
    val appVersion = remember { BundleUtils.getAppVersion(platformLauncher) }

    val aboutTitle = stringResource(Res.string.ABOUT_PRODUCT_SCREEN_TITLE_FORMAT, BundleUtils.getAppName(platformLauncher))
    val legalTitle = stringResource(Res.string.LEGAL_PRODUCT_SCREEN_TITLE)

    SettingsScreenContent(
        state = state,
        baseAppConfig = baseAppConfig,
        appVersion = appVersion,
        isStage = flavour == Flavour.Stage,
        onNameClicked = eventDelegate::onNameClicked,
        onAgeClicked = eventDelegate::onAgeClicked,
        onHeightClicked = eventDelegate::onHeightClicked,
        onInitialWeightClicked = eventDelegate::onInitialWeightClicked,
        onTargetWeightClicked = eventDelegate::onTargetWeightClicked,
        onContactUsClicked = {
            scope.launch {
                globalProgressIndicator.value = true
                eventDelegate.onContactUsClicked()
                globalProgressIndicator.value = false
            }
        },
        onAboutCalorieCountClicked = {
            eventDelegate.onSettingsMenuClicked("about_product", aboutTitle)
        },
        onLegalClicked = { eventDelegate.onSettingsMenuClicked("legal_product", legalTitle) },
        onMealPlanClicked = eventDelegate::onMealPlanClicked,
        onBackClicked = eventDelegate::onBackClicked,
        onAccountDeleteClicked = { showAccountDeleteConfirmation.value = true },
        onLogoutClicked = viewModel::onLogoutClicked,
        onHealthKitClicked = eventDelegate::onHealthKitClicked,
        onManageSubscriptionClicked = {
            appAnalytics.logEvent(Events.UserSubscriptionManagementClicked)
            if (state.isMobilePurchase) {
                val url = when (targetPlatform()) {
                    TargetPlatform.Android -> "https://play.google.com/store/account/subscriptions"
                    TargetPlatform.iOS -> "itms-apps://apps.apple.com/account/subscriptions"
                    TargetPlatform.Web -> "https://healthium.one"
                }
                uriHandler.openUri(url)
            } else {
                val subscriptionManagementUrl = state.subscriptionManagementUrl
                if (subscriptionManagementUrl != null) {
                    browser.openUrl(platformLauncher, subscriptionManagementUrl)
                }
            }
        },
    )

    AccountDeleteConfirmation(
        show = showAccountDeleteConfirmation,
        onDeleteClicked = viewModel::onDeleteAccountClicked,
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun SettingsScreenContent(
    state: SettingsUiState,
    baseAppConfig: BaseAppConfig,
    appVersion: String,
    isStage: Boolean,
    onNameClicked: () -> Unit,
    onAgeClicked: () -> Unit,
    onHeightClicked: () -> Unit,
    onInitialWeightClicked: () -> Unit,
    onTargetWeightClicked: () -> Unit,
    onContactUsClicked: () -> Unit,
    onAboutCalorieCountClicked: () -> Unit,
    onLegalClicked: () -> Unit,
    onMealPlanClicked: () -> Unit,
    onBackClicked: () -> Unit,
    onAccountDeleteClicked: () -> Unit,
    onLogoutClicked: () -> Unit,
    onHealthKitClicked: () -> Unit,
    onManageSubscriptionClicked: () -> Unit,
) {
    val currentYear = remember(Unit) {
        Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).year
    }

    AppScaffold(
        topBar = {
            AppTopBar(
                title = {
                    Text(stringResource(Res.string.SETTINGS_SCREEN_TITLE))
                },
                navigationIcon = { AppBackNavigationButton(onClick = onBackClicked) },
            )
        }
    ) {
        Column(
            modifier = Modifier
                .padding(it)
                .verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Section(stringResource(Res.string.SETTINGS_PROFILE_SECTION))
            SettingsItem(
                label = stringResource(Res.string.SETTINGS_PROFILE_NAME_LABEL),
                trailing = { ArrowIcon() },
                onClick = onNameClicked,
            )
            HorizontalDivider()
            SettingsItem(
                label = stringResource(Res.string.SETTINGS_PROFILE_AGE_LABEL),
                trailing = { ArrowIcon() },
                onClick = onAgeClicked,
            )
            HorizontalDivider()
            SettingsItem(
                label = stringResource(Res.string.SETTINGS_PROFILE_HEIGHT_LABEL),
                trailing = { ArrowIcon() },
                onClick = onHeightClicked,
            )
            HorizontalDivider()
            SettingsItem(
                label = stringResource(Res.string.SETTINGS_PROFILE_INITIAL_WEIGHT_LABEL),
                trailing = { ArrowIcon() },
                onClick = onInitialWeightClicked,
            )
            HorizontalDivider()
            SettingsItem(
                label = stringResource(Res.string.SETTINGS_PROFILE_TARGET_WEIGHT_LABEL),
                trailing = { ArrowIcon() },
                onClick = onTargetWeightClicked,
            )
            HorizontalDivider()
            if (baseAppConfig.features.mealPlansEnabled) {
                SettingsItem(
                    label = stringResource(Res.string.SETTINGS_MEAL_PLAN_LABEL),
                    trailing = { ArrowIcon() },
                    onClick = onMealPlanClicked,
                )
                HorizontalDivider()
            }
            MetricSystemItem()
            HorizontalDivider()
            if (baseAppConfig.features.periodTrackerEnabled) {
                PeriodTrackerItem()
                HorizontalDivider()
            }
            Section(stringResource(Res.string.SETTINGS_GENERAL_SECTION))
            if (state.manageSubscriptionEnabled) {
                SettingsItem(
                    label = stringResource(Res.string.SETTINGS_MANAGE_SUBSCRIPTION_LABEL),
                    trailing = { ArrowIcon() },
                    onClick = onManageSubscriptionClicked,
                )
                HorizontalDivider()
            }
            SettingsItem(
                label = stringResource(Res.string.SETTINGS_CONTACT_US_LABEL),
                trailing = { ArrowIcon() },
                onClick = onContactUsClicked,
            )
            HorizontalDivider()
            SettingsItem(
                label = stringResource(Res.string.SETTINGS_ABOUT_PRODUCT_LABEL),
                trailing = { ArrowIcon() },
                onClick = onAboutCalorieCountClicked,
            )
            HorizontalDivider()
            SettingsItem(
                label = stringResource(Res.string.SETTINGS_LEGAL_PRODUCT_LABEL),
                trailing = { ArrowIcon() },
                onClick = onLegalClicked,
            )
            if (baseAppConfig.features.healthKitEnabled()) {
                HorizontalDivider()
                SettingsItem(
                    label = ConnectBannerHelper.healthKitName,
                    trailing = { ArrowIcon() },
                    onClick = onHealthKitClicked,
                )
            }

            HorizontalDivider()
            SettingsItem(
                label = stringResource(Res.string.SETTINGS_DELETE_ACCOUNT_LABEL),
                trailing = { ArrowIcon() },
                onClick = onAccountDeleteClicked,
                textColor = red_1,
            )
            LogoutButton(onLogoutClicked)
            Spacer(Modifier.height(24.dp))
            Text(
                text = "© $currentYear Healthium, Inc.\n$appVersion${if (isStage) " Stage" else ""}",
                style = AppTheme.textStyles.secondary_14_400,
                color = AppTheme.colors.text300,
                textAlign = TextAlign.Center
            )
            Spacer(Modifier.height(100.dp))
        }
    }
}

@Composable
private fun LogoutButton(onClick: () -> Unit) {
    val flavour = koinInject<Flavour>()
    if (flavour == Flavour.Stage) {
        HorizontalDivider()
        SettingsItem(
            label = stringResource(Res.string.SETTINGS_LOGOUT_LABEL),
            trailing = { ArrowIcon() },
            onClick = onClick,
            textColor = red_1,
        )
    }
}

@Composable
private fun MetricSystemItem() {
    val appPreferences = koinInject<AppPreferences>()
    SettingsItem(
        label = stringResource(Res.string.SETTINGS_METRICS_SYSTEM_LABEL),
        trailing = {
            Switch(
                checked = appPreferences.isMetricFlow.collectAsState().value,
                onCheckedChange = { checked ->
                    appPreferences.isMetric = checked
                },
            )
        },
        onClick = { appPreferences.isMetric = appPreferences.isMetric.not() },
    )
}

@Composable
private fun PeriodTrackerItem() {
    val appPreferences = koinInject<AppPreferences>()
    SettingsItem(
        label = stringResource(Res.string.SETTINGS_PERIOD_TRACKER_LABEL),
        trailing = {
            Switch(
                checked = appPreferences.periodTrackerEnabledFlow.collectAsState().value,
                onCheckedChange = { checked ->
                    appPreferences.periodTrackerEnabled = checked
                },
            )
        },
        onClick = { appPreferences.periodTrackerEnabled = appPreferences.periodTrackerEnabled.not() },
    )
}

@Composable
private fun AccountDeleteConfirmation(
    show: MutableState<Boolean>,
    onDeleteClicked: () -> Unit,
) {
    if (show.value) {
        AppAlertDialog(
            positiveButton = stringResource(Res.string.SETTINGS_DELETE_ACCOUNT_POSITIVE_BUTTON),
            negativeButton = stringResource(Res.string.SETTINGS_DELETE_ACCOUNT_NEGATIVE_BUTTON),
            onPositiveClick = {
                show.value = false
                onDeleteClicked()
            },
            onNegativeClick = { show.value = false },
            positiveButtonType = AlertButtonType.DESTRUCTIVE,
            title = stringResource(Res.string.SETTINGS_DELETE_ACCOUNT_ALERT_TITLE),
            text = stringResource(Res.string.SETTINGS_DELETE_ACCOUNT_ALERT_MESSAGE),
            onDismissRequest = {
                show.value = false
            }
        )
    }
}
